// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 42-app-marketplace.md
import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

import { AppFavoriteToggle } from '@/components/apps-list/AppFavoriteToggle';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { api } from '@/lib/api';
import { mutationErrorMessage } from '@/lib/apiErrors';
import { type MarketplaceApp } from '@greenlight/shared';

const MARKETPLACE_KEY = ['marketplace'] as const;

interface MarketplaceCardActionsProps {
  app: MarketplaceApp;
}

/**
 * Per-card personalization for the marketplace grid (docs/42 §Personalization):
 * favorite pins a card to the leading tier, hide drops it from the caller's own
 * catalog. Both write the unaudited `app_user_prefs` flags — neither changes
 * listing or access, so the copy says "for you" and the
 * card never grows an unlist or management control.
 *
 * Rendered as an overlay outside the card's wrapping Open link: an accessible
 * card is one big anchor, and a button nested in an anchor is neither valid nor
 * operable.
 */
export function MarketplaceCardActions({ app }: MarketplaceCardActionsProps): React.JSX.Element {
  const queryClient = useQueryClient();
  const setHidden = api.apps.setHidden.useMutation();
  const unsetHidden = api.apps.unsetHidden.useMutation();
  const pending = setHidden.isPending || unsetHidden.isPending;
  // The dashboard read always personalizes, so the flags are present; they are
  // optional on the shared card because MCP's unpersonalized read omits them.
  const hidden = app.hidden ?? false;
  const label = hidden ? `Show ${app.name} in your marketplace` : `Hide ${app.name} for you`;

  const toggleHidden = async (hide: boolean): Promise<void> => {
    const res = hide
      ? await setHidden.mutateAsync({ params: { id: app.app_id } })
      : await unsetHidden.mutateAsync({ params: { id: app.app_id } });
    if (res.status >= 300) throw res;
    await queryClient.invalidateQueries({ queryKey: [...MARKETPLACE_KEY] });
  };

  const onClick = async (e: React.MouseEvent): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();
    if (pending) return;
    const hide = !hidden;
    try {
      await toggleHidden(hide);
      // Hiding removes the card from the grid, so the toast is the only way
      // back without switching on Show hidden.
      toast.success(hide ? `${app.name} hidden for you.` : `${app.name} is back in your view.`, {
        action: hide
          ? {
              label: 'Undo',
              onClick: () => {
                void toggleHidden(false).catch((err: unknown) =>
                  toast.error(`Couldn't undo: ${mutationErrorMessage(err)}`),
                );
              },
            }
          : undefined,
      });
    } catch (err) {
      toast.error(`Couldn't update: ${mutationErrorMessage(err)}`);
    }
  };

  const HiddenIcon = hidden ? Eye : EyeOff;

  return (
    <div className="absolute right-2 top-2 z-10 flex items-center gap-0.5 rounded-full border border-border/60 bg-background/85 p-0.5 shadow-sm backdrop-blur">
      <AppFavoriteToggle
        appId={app.app_id}
        appName={app.name}
        favorite={app.favorite ?? false}
        invalidateKey={MARKETPLACE_KEY}
        className="h-11 w-11 rounded-full md:h-8 md:w-8"
      />
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-11 w-11 rounded-full md:h-8 md:w-8"
              aria-pressed={hidden}
              aria-label={label}
              disabled={pending}
              onClick={(e) => void onClick(e)}
            >
              <HiddenIcon className="size-4 text-muted-foreground" aria-hidden />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{label}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}
